import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Stack from '@mui/material/Stack';

/** Matches MUI Button size="large" / ProfileToolbar height. */
const BOT_BAR_HEIGHT = 42;

const TELEGRAM_ROUTE = '/teacher/bot/telegram';
const INSTAGRAM_CAMPAIGNS_ROUTE = '/teacher/bot/instagram/campaigns';

const isBotRoute = (pathname) => pathname.startsWith('/teacher/bot/');
const isInstagramRoute = (pathname) =>
  pathname.startsWith('/teacher/bot/instagram/');

/**
 * Teacher BOT chrome group (sibling pattern to ProfileToolbar).
 * BOT → TELEGRAM | INSTAGRAM → CAMPAIGNS (Instagram children).
 */
const BotToolbar = () => {
  const location = useLocation();
  const routePinned = isBotRoute(location.pathname);
  const instagramPinned = isInstagramRoute(location.pathname);
  const [open, setOpen] = useState(routePinned);
  const [instagramOpen, setInstagramOpen] = useState(instagramPinned);

  useEffect(() => {
    setOpen(isBotRoute(location.pathname));
    setInstagramOpen(isInstagramRoute(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setInstagramOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (!routePinned) {
          setOpen(false);
          setInstagramOpen(false);
        }
      }}
    >
      <Box
        sx={{
          height: BOT_BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          ml: 2,
          pl: 0.5,
          pr: 0.5,
          borderRadius: 2,
          backgroundColor: open
            ? 'rgba(0, 0, 0, 0.22)'
            : 'rgba(0, 0, 0, 0.12)',
          boxShadow: open
            ? '0 4px 14px rgba(0, 0, 0, 0.35), inset 0 0 0 1px rgba(255,255,255,0.18)'
            : '0 1px 4px rgba(0, 0, 0, 0.2), inset 0 0 0 1px rgba(255,255,255,0.1)',
          transition: 'background-color 200ms ease, box-shadow 200ms ease',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ height: BOT_BAR_HEIGHT }}
        >
          <Button
            variant="contained"
            color={routePinned ? 'warning' : 'secondary'}
            size="large"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-label="Bot menu"
            sx={{
              height: BOT_BAR_HEIGHT,
              whiteSpace: 'nowrap',
              minWidth: 72,
              boxShadow: open ? '0 0 0 2px rgba(255,255,255,0.85)' : 'none',
            }}
          >
            BOT
          </Button>

          <Collapse
            in={open}
            orientation="horizontal"
            timeout={280}
            unmountOnExit
            sx={{
              height: BOT_BAR_HEIGHT,
              alignSelf: 'center',
              '& .MuiCollapse-wrapper': { height: BOT_BAR_HEIGHT },
              '& .MuiCollapse-wrapperInner': {
                height: BOT_BAR_HEIGHT,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ height: BOT_BAR_HEIGHT, pl: 0.5 }}
            >
              <Button
                variant="contained"
                color={isActive(TELEGRAM_ROUTE) ? 'warning' : 'primary'}
                size="large"
                component={Link}
                to={TELEGRAM_ROUTE}
                sx={{ height: BOT_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                TELEGRAM
              </Button>
              <Button
                variant="contained"
                color={instagramPinned ? 'warning' : 'primary'}
                size="large"
                onClick={() => setInstagramOpen((prev) => !prev)}
                aria-expanded={instagramOpen}
                sx={{ height: BOT_BAR_HEIGHT, whiteSpace: 'nowrap' }}
              >
                INSTAGRAM
              </Button>

              <Collapse
                in={instagramOpen}
                orientation="horizontal"
                timeout={280}
                unmountOnExit
                sx={{
                  height: BOT_BAR_HEIGHT,
                  alignSelf: 'center',
                  '& .MuiCollapse-wrapper': { height: BOT_BAR_HEIGHT },
                  '& .MuiCollapse-wrapperInner': {
                    height: BOT_BAR_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ height: BOT_BAR_HEIGHT, pl: 0.5 }}
                >
                  <Button
                    variant="contained"
                    color={
                      isActive(INSTAGRAM_CAMPAIGNS_ROUTE) ? 'warning' : 'info'
                    }
                    size="large"
                    component={Link}
                    to={INSTAGRAM_CAMPAIGNS_ROUTE}
                    sx={{ height: BOT_BAR_HEIGHT, whiteSpace: 'nowrap' }}
                  >
                    CAMPAIGNS
                  </Button>
                </Stack>
              </Collapse>
            </Stack>
          </Collapse>
        </Stack>
      </Box>
    </ClickAwayListener>
  );
};

export default BotToolbar;
